/**
 * BookForm - Formularz dodawania/edycji książek
 * wojskaspecjalne.pl
 */

import React, { useState, useEffect } from 'react';
import {
  createBook,
  updateBook,
  getBookById,
  getBookFiles,
  getAllAuthors,
  createAuthor,
  getAllTags,
  getAllCategories,
  getBookTags,
  getBookCategories,
  createTag,
  createCategory,
  type CreateBookData,
  type Book,
  type Author,
  type BookTag,
  type BookCategory,
} from '../../lib/libraryService';
import { useAuth } from '../../contexts/AuthContext';
import { FileUploadManager } from './FileUploadManager';
import './BookForm.css';

interface BookFormProps {
  bookId?: string; // Jeśli jest, to edytujemy
  onSuccess?: (book: Book) => void;
  onCancel?: () => void;
}

export const BookForm: React.FC<BookFormProps> = ({ bookId, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(!!bookId);
  const [error, setError] = useState<string | null>(null);
  const [existingFiles, setExistingFiles] = useState<any[]>([]);

  // Autorzy
  const [allAuthors, setAllAuthors] = useState<Author[]>([]);
  const [loadingAuthors, setLoadingAuthors] = useState(true);
  const [selectedAuthorIds, setSelectedAuthorIds] = useState<string[]>([]);
  const [showNewAuthorForm, setShowNewAuthorForm] = useState(false);
  const [newAuthorData, setNewAuthorData] = useState({ name: '', bio: '', website_url: '' });

  // Tags & Categories state
  const [availableTags, setAvailableTags] = useState<BookTag[]>([]);
  const [availableCategories, setAvailableCategories] = useState<BookCategory[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [showCategoryInput, setShowCategoryInput] = useState(false);

  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    summary: string;
    external_link: string;
    cover_image_url: string;
    isbn: string;
    publication_year: string;
    publisher: string;
    language: string;
    pages: string;
    status: 'draft' | 'published';
  }>({
    title: '',
    description: '',
    summary: '',
    external_link: '',
    cover_image_url: '',
    isbn: '',
    publication_year: '',
    publisher: '',
    language: 'pl',
    pages: '',
    status: 'draft',
  });

  // Load authors
  useEffect(() => {
    loadAuthors();
  }, []);

  // Load tags and categories on mount
  useEffect(() => {
    loadTagsAndCategories();
  }, []);

  // Load book data if editing
  useEffect(() => {
    if (bookId) {
      loadBookData();
    }
  }, [bookId]);

  const loadTagsAndCategories = async () => {
    try {
      const [tagsResult, categoriesResult] = await Promise.all([
        getAllTags({ sortByUsage: true }),
        getAllCategories({ sortByUsage: true }),
      ]);

      if (tagsResult.success && tagsResult.data) {
        setAvailableTags(tagsResult.data);
      }

      if (categoriesResult.success && categoriesResult.data) {
        setAvailableCategories(categoriesResult.data);
      }
    } catch (err) {
      console.error('Error loading tags/categories:', err);
    }
  };

  const loadAuthors = async () => {
    try {
      setLoadingAuthors(true);
      const result = await getAllAuthors();
      if (result.success && result.data) {
        setAllAuthors(result.data);
      }
    } catch (err) {
      console.error('Error loading authors:', err);
    } finally {
      setLoadingAuthors(false);
    }
  };

  const loadBookData = async () => {
    if (!bookId) return;

    try {
      setLoadingData(true);
      const result = await getBookById(bookId);

      if (result.success && result.data) {
        const book = result.data;

        setFormData({
          title: book.title,
          description: book.description || '',
          summary: book.summary || '',
          external_link: book.external_link || '',
          cover_image_url: book.cover_image_url || '',
          isbn: book.isbn || '',
          publication_year: book.publication_year?.toString() || '',
          publisher: book.publisher || '',
          language: book.language || 'pl',
          pages: book.pages?.toString() || '',
          status: book.status,
        });

        // Ustaw wybranych autorów
        if (book.authors && book.authors.length > 0) {
          setSelectedAuthorIds(book.authors.map((a) => a.id));
        }

        // Load existing files
        const filesResult = await getBookFiles(bookId);
        if (filesResult.success && filesResult.data) {
          setExistingFiles(filesResult.data);
        }

        // Load existing tags and categories for this book
        const [tagsResult, categoriesResult] = await Promise.all([
          getBookTags(bookId),
          getBookCategories(bookId),
        ]);

        if (tagsResult.success && tagsResult.data) {
          setSelectedTagIds(tagsResult.data.map(t => t.id));
        }

        if (categoriesResult.success && categoriesResult.data) {
          setSelectedCategoryIds(categoriesResult.data.map(c => c.id));
        }
      } else {
        setError(result.error || 'Nie udało się załadować danych książki');
      }
    } catch (err) {
      console.error('Error loading book:', err);
      setError('Wystąpił błąd podczas ładowania danych');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAuthorSelect = (authorId: string) => {
    setSelectedAuthorIds((prev) => {
      if (prev.includes(authorId)) {
        return prev.filter((id) => id !== authorId);
      } else {
        return [...prev, authorId];
      }
    });
  };

  const handleMoveAuthorUp = (index: number) => {
    if (index === 0) return;
    setSelectedAuthorIds((prev) => {
      const newIds = [...prev];
      [newIds[index - 1], newIds[index]] = [newIds[index], newIds[index - 1]];
      return newIds;
    });
  };

  const handleMoveAuthorDown = (index: number) => {
    if (index === selectedAuthorIds.length - 1) return;
    setSelectedAuthorIds((prev) => {
      const newIds = [...prev];
      [newIds[index], newIds[index + 1]] = [newIds[index + 1], newIds[index]];
      return newIds;
    });
  };

  const handleRemoveAuthor = (authorId: string) => {
    setSelectedAuthorIds((prev) => prev.filter((id) => id !== authorId));
  };

  const handleAddNewAuthor = async () => {
    if (!newAuthorData.name.trim()) {
      alert('Imię i nazwisko autora jest wymagane');
      return;
    }

    try {
      const result = await createAuthor({
        name: newAuthorData.name.trim(),
        bio: newAuthorData.bio.trim() || undefined,
        website_url: newAuthorData.website_url.trim() || undefined,
      });

      if (result.success && result.data) {
        // Dodaj do listy autorów
        setAllAuthors((prev) => [...prev, result.data!].sort((a, b) => a.name.localeCompare(b.name)));
        // Automatycznie wybierz nowo dodanego autora
        setSelectedAuthorIds((prev) => [...prev, result.data!.id]);
        // Reset formularza
        setNewAuthorData({ name: '', bio: '', website_url: '' });
        setShowNewAuthorForm(false);
      } else {
        alert(result.error || 'Nie udało się dodać autora');
      }
    } catch (err: any) {
      console.error('Error adding author:', err);
      alert('Wystąpił błąd podczas dodawania autora');
    }
  };

  const handleToggleTag = (tagId: string) => {
    setSelectedTagIds(prev => 
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleToggleCategory = (categoryId: string) => {
    setSelectedCategoryIds(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleAddNewTag = async () => {
    if (!newTagName.trim()) return;

    try {
      const result = await createTag({ name: newTagName.trim() });
      if (result.success && result.data) {
        setAvailableTags(prev => [...prev, result.data!]);
        setSelectedTagIds(prev => [...prev, result.data!.id]);
        setNewTagName('');
        setShowTagInput(false);
      } else {
        setError(result.error || 'Nie udało się dodać tagu');
      }
    } catch (err: any) {
      setError(err.message || 'Błąd podczas dodawania tagu');
    }
  };

  const handleAddNewCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const result = await createCategory({ name: newCategoryName.trim() });
      if (result.success && result.data) {
        setAvailableCategories(prev => [...prev, result.data!]);
        setSelectedCategoryIds(prev => [...prev, result.data!.id]);
        setNewCategoryName('');
        setShowCategoryInput(false);
      } else {
        setError(result.error || 'Nie udało się dodać kategorii');
      }
    } catch (err: any) {
      setError(err.message || 'Błąd podczas dodawania kategorii');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Validate
      if (!formData.title.trim()) {
        throw new Error('Tytuł jest wymagany');
      }

      if (selectedAuthorIds.length === 0) {
        throw new Error('Wybierz przynajmniej jednego autora');
      }

      // Prepare authors data
      const authors = selectedAuthorIds.map((authorId, index) => {
        const author = allAuthors.find(a => a.id === authorId);
        if (!author) throw new Error(`Nie znaleziono autora o ID: ${authorId}`);
        return {
          name: author.name,
          bio: author.bio || undefined,
          order: index,
        };
      });

      // Prepare data
      const bookData: CreateBookData = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        summary: formData.summary.trim() || undefined,
        external_link: formData.external_link.trim() || undefined,
        cover_image_url: formData.cover_image_url.trim() || undefined,
        isbn: formData.isbn.trim() || undefined,
        publication_year: formData.publication_year ? parseInt(formData.publication_year) : undefined,
        publisher: formData.publisher.trim() || undefined,
        language: formData.language || 'pl',
        pages: formData.pages ? parseInt(formData.pages) : undefined,
        status: formData.status,
        authors: authors,
        tag_ids: selectedTagIds,
        category_ids: selectedCategoryIds,
      };

      let result;
      if (bookId) {
        result = await updateBook({ id: bookId, ...bookData });
      } else {
        result = await createBook(bookData, user.id);
      }

      if (result.success && result.data) {
        onSuccess?.(result.data);
      } else {
        setError(result.error || 'Wystąpił błąd podczas zapisywania książki');
      }
    } catch (err: any) {
      console.error('Error submitting book:', err);
      setError(err.message || 'Wystąpił błąd podczas zapisywania książki');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData || loadingAuthors) {
    return (
      <div className="book-form-container">
        <div className="book-form-loading">Ładowanie danych...</div>
      </div>
    );
  }

  const selectedAuthors = selectedAuthorIds
    .map((id) => allAuthors.find((a) => a.id === id))
    .filter(Boolean) as Author[];

  return (
    <div className="book-form-container">
      <form onSubmit={handleSubmit} className="book-form">
        <div className="book-form-header">
          <h2>{bookId ? 'Edytuj Książkę' : 'Dodaj Nową Książkę'}</h2>
        </div>

        {error && (
          <div className="book-form-error">
            {error}
          </div>
        )}

        <div className="book-form-grid">
          {/* Basic Info */}
          <div className="book-form-section">
            <h3>Podstawowe informacje</h3>

            <div className="form-field">
              <label htmlFor="title">
                Tytuł <span className="required">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Wprowadź tytuł książki"
              />
            </div>

            <div className="form-field">
              <label htmlFor="description">Opis</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="Krótki opis książki"
              />
            </div>

            <div className="form-field">
              <label htmlFor="summary">Streszczenie</label>
              <textarea
                id="summary"
                name="summary"
                value={formData.summary}
                onChange={handleChange}
                rows={6}
                placeholder="Szczegółowe streszczenie treści"
              />
            </div>

          </div>

          {/* Publication Details */}
          <div className="book-form-section">
            <h3>Szczegóły publikacji</h3>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="publication_year">Rok wydania</label>
                <input
                  type="number"
                  id="publication_year"
                  name="publication_year"
                  value={formData.publication_year}
                  onChange={handleChange}
                  min="1900"
                  max={new Date().getFullYear()}
                  placeholder="2024"
                />
              </div>

              <div className="form-field">
                <label htmlFor="pages">Liczba stron</label>
                <input
                  type="number"
                  id="pages"
                  name="pages"
                  value={formData.pages}
                  onChange={handleChange}
                  min="1"
                  placeholder="250"
                />
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="publisher">Wydawca</label>
              <input
                type="text"
                id="publisher"
                name="publisher"
                value={formData.publisher}
                onChange={handleChange}
                placeholder="Nazwa wydawcy"
              />
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="isbn">ISBN</label>
                <input
                  type="text"
                  id="isbn"
                  name="isbn"
                  value={formData.isbn}
                  onChange={handleChange}
                  placeholder="978-83-XXXXX-XX-X"
                />
              </div>

              <div className="form-field">
                <label htmlFor="language">Język</label>
                <select
                  id="language"
                  name="language"
                  value={formData.language}
                  onChange={handleChange}
                >
                  <option value="pl">Polski</option>
                  <option value="en">Angielski</option>
                  <option value="de">Niemiecki</option>
                  <option value="fr">Francuski</option>
                  <option value="es">Hiszpański</option>
                </select>
              </div>
            </div>
          </div>

          {/* Classification */}
          <div className="book-form-section">
            <h3>Klasyfikacja</h3>

            {/* Categories */}
            <div className="form-field">
              <label>Kategorie</label>
              <div className="tags-selector">
                <div className="tags-list">
                  {availableCategories.map(category => (
                    <button
                      key={category.id}
                      type="button"
                      className={`tag-item ${selectedCategoryIds.includes(category.id) ? 'selected' : ''}`}
                      onClick={() => handleToggleCategory(category.id)}
                      style={{
                        borderColor: selectedCategoryIds.includes(category.id) ? category.color : undefined,
                        backgroundColor: selectedCategoryIds.includes(category.id) ? `${category.color}20` : undefined,
                      }}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
                
                {showCategoryInput ? (
                  <div className="add-tag-input">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Nazwa nowej kategorii"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddNewCategory())}
                    />
                    <button type="button" onClick={handleAddNewCategory} className="btn-add-confirm">
                      Dodaj
                    </button>
                    <button type="button" onClick={() => {setShowCategoryInput(false); setNewCategoryName('');}} className="btn-cancel">
                      Anuluj
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn-add-tag"
                    onClick={() => setShowCategoryInput(true)}
                  >
                    + Dodaj nową kategorię
                  </button>
                )}
                
                {selectedCategoryIds.length > 0 && (
                  <small className="selected-count">
                    Wybrano: {selectedCategoryIds.length}
                  </small>
                )}
              </div>
            </div>

            {/* Tags */}
            <div className="form-field">
              <label>Tagi</label>
              <div className="tags-selector">
                <div className="tags-list">
                  {availableTags.map(tag => (
                    <button
                      key={tag.id}
                      type="button"
                      className={`tag-item ${selectedTagIds.includes(tag.id) ? 'selected' : ''}`}
                      onClick={() => handleToggleTag(tag.id)}
                      style={{
                        borderColor: selectedTagIds.includes(tag.id) ? tag.color : undefined,
                        backgroundColor: selectedTagIds.includes(tag.id) ? `${tag.color}20` : undefined,
                      }}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
                
                {showTagInput ? (
                  <div className="add-tag-input">
                    <input
                      type="text"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      placeholder="Nazwa nowego tagu"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddNewTag())}
                    />
                    <button type="button" onClick={handleAddNewTag} className="btn-add-confirm">
                      Dodaj
                    </button>
                    <button type="button" onClick={() => {setShowTagInput(false); setNewTagName('');}} className="btn-cancel">
                      Anuluj
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn-add-tag"
                    onClick={() => setShowTagInput(true)}
                  >
                    + Dodaj nowy tag
                  </button>
                )}
                
                {selectedTagIds.length > 0 && (
                  <small className="selected-count">
                    Wybrano: {selectedTagIds.length}
                  </small>
                )}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="draft">Szkic (niepublikowana)</option>
                <option value="published">Opublikowana</option>
              </select>
            </div>
          </div>

          {/* Media */}
          <div className="book-form-section">
            <h3>Multimedia i linki</h3>

            <div className="form-field">
              <label htmlFor="cover_image_url">URL okładki</label>
              <input
                type="url"
                id="cover_image_url"
                name="cover_image_url"
                value={formData.cover_image_url}
                onChange={handleChange}
                placeholder="https://example.com/cover.jpg"
              />
            </div>

            <div className="form-field">
              <label htmlFor="external_link">Link zewnętrzny</label>
              <input
                type="url"
                id="external_link"
                name="external_link"
                value={formData.external_link}
                onChange={handleChange}
                placeholder="https://example.com/book"
              />
              <small>Link do pełnej treści, sklepu itp.</small>
            </div>
          </div>

          {/* Authors Section */}
          <div className="book-form-section book-form-authors">
            <h3>
              Autorzy <span className="required">*</span>
            </h3>

            {/* Selected Authors List */}
            {selectedAuthors.length > 0 && (
              <div className="selected-authors-list">
                <h4>Wybrani autorzy ({selectedAuthors.length})</h4>
                {selectedAuthors.map((author, index) => (
                  <div key={author.id} className="selected-author-item">
                    <div className="author-info">
                      <strong>{author.name}</strong>
                      {author.bio && <small>{author.bio.substring(0, 100)}...</small>}
                    </div>
                    <div className="author-actions">
                      <button
                        type="button"
                        onClick={() => handleMoveAuthorUp(index)}
                        disabled={index === 0}
                        className="btn-icon"
                        title="Przesuń w górę"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveAuthorDown(index)}
                        disabled={index === selectedAuthors.length - 1}
                        className="btn-icon"
                        title="Przesuń w dół"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveAuthor(author.id)}
                        className="btn-icon btn-remove"
                        title="Usuń"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Authors Selection */}
            <div className="authors-selection">
              <h4>Wybierz autorów z listy</h4>
              <div className="authors-grid">
                {allAuthors.map((author) => {
                  const isSelected = selectedAuthorIds.includes(author.id);
                  return (
                    <div
                      key={author.id}
                      className={`author-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleAuthorSelect(author.id)}
                    >
                      <div className="author-card-content">
                        <strong>{author.name}</strong>
                        {author.bio && <small>{author.bio.substring(0, 80)}...</small>}
                      </div>
                      {isSelected && <span className="checkmark">✓</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Add New Author */}
            <div className="add-new-author">
              {!showNewAuthorForm ? (
                <button
                  type="button"
                  onClick={() => setShowNewAuthorForm(true)}
                  className="btn-add-author"
                >
                  + Dodaj nowego autora
                </button>
              ) : (
                <div className="new-author-form">
                  <h4>Dodaj nowego autora</h4>
                  <div className="form-field">
                    <label>
                      Imię i nazwisko <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={newAuthorData.name}
                      onChange={(e) =>
                        setNewAuthorData((prev) => ({ ...prev, name: e.target.value }))
                      }
                      placeholder="Jan Kowalski"
                    />
                  </div>
                  <div className="form-field">
                    <label>Biografia</label>
                    <textarea
                      value={newAuthorData.bio}
                      onChange={(e) =>
                        setNewAuthorData((prev) => ({ ...prev, bio: e.target.value }))
                      }
                      rows={3}
                      placeholder="Krótka biografia autora"
                    />
                  </div>
                  <div className="form-field">
                    <label>Strona internetowa</label>
                    <input
                      type="url"
                      value={newAuthorData.website_url}
                      onChange={(e) =>
                        setNewAuthorData((prev) => ({ ...prev, website_url: e.target.value }))
                      }
                      placeholder="https://example.com"
                    />
                  </div>
                  <div className="new-author-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewAuthorForm(false);
                        setNewAuthorData({ name: '', bio: '', website_url: '' });
                      }}
                      className="btn-cancel"
                    >
                      Anuluj
                    </button>
                    <button
                      type="button"
                      onClick={handleAddNewAuthor}
                      className="btn-submit"
                    >
                      Dodaj autora
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Files Section - tylko przy edycji */}
          {bookId && (
            <div className="book-form-section book-form-files">
              <FileUploadManager
                bookId={bookId}
                existingFiles={existingFiles}
                disabled={loading}
              />
            </div>
          )}
        </div>

        {!bookId && (
          <div className="book-form-notice">
            <p>
              <strong>💡 Uwaga:</strong> Pliki można będzie dodać po utworzeniu książki.
            </p>
          </div>
        )}

        <div className="book-form-actions">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="btn-cancel"
              disabled={loading}
            >
              Anuluj
            </button>
          )}
          <button type="submit" className="btn-submit" disabled={loading}>
            {loading ? 'Zapisywanie...' : bookId ? 'Zaktualizuj' : 'Dodaj książkę'}
          </button>
        </div>
      </form>
    </div>
  );
};
